import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import QuickDateFilters, { ViewMode } from '../components/KidsClub/QuickDateFilters';
import DayDetailDialog from '../components/swim/DayDetailDialog';
import SessionDetailDialog from '../components/swim/SessionDetailDialog';
import MeetDetailDialog from '../components/swim/MeetDetailDialog';
import EventDetailDialog from '../components/swim/EventDetailDialog';
import swimService from '../api/services/swimService';
import proBookingService from '../api/services/proBookingService';
import { CoachCalendarDay, CoachCalendarSession, SwimCompetitionRow, SwimEventRow } from '../types/swim';
import { Pro } from '../types';

const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/** Local (NOT UTC) YYYY-MM-DD — avoids the off-by-one where end-of-day-local crosses midnight UTC. */
function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfWeekSunday(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  out.setDate(out.getDate() - out.getDay());
  return out;
}

function startOfMonth(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), 1);
  out.setHours(0, 0, 0, 0);
  return out;
}
function endOfMonth(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  out.setHours(23, 59, 59, 999);
  return out;
}

function computeRange(anchor: Date, view: ViewMode): { start: Date; end: Date } {
  if (view === 'day') {
    const start = new Date(anchor);
    start.setHours(0, 0, 0, 0);
    const end = new Date(anchor);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }
  if (view === 'week') {
    const start = startOfWeekSunday(anchor);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start, end };
  }
  const monthStart = startOfMonth(anchor);
  const monthEnd = endOfMonth(anchor);
  const start = startOfWeekSunday(monthStart);
  const end = new Date(monthEnd);
  const tailDays = 6 - end.getDay();
  end.setDate(end.getDate() + tailDays);
  return { start, end };
}

function rangeLabel(anchor: Date, view: ViewMode): string {
  const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const fullFmt = (d: Date) => d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const monthFmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  if (view === 'day') return fullFmt(anchor);
  if (view === 'week') {
    const start = startOfWeekSunday(anchor);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${fmt(start)} – ${fmt(end)}, ${start.getFullYear()}`;
  }
  return monthFmt(anchor);
}

/** "HH:MM[:SS]" or an ISO datetime → friendly 12-hour time. */
function fmtTime(value: string | null): string {
  if (!value) return '';
  const d = value.includes('T') ? new Date(value) : new Date(`1970-01-01T${value}`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

const CoachSchedulePage: React.FC = () => {
  const theme = useTheme();

  const [view, setView] = useState<ViewMode>('month');
  const [anchor, setAnchor] = useState<Date>(new Date());
  const [days, setDays] = useState<CoachCalendarDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDay, setOpenDay] = useState<CoachCalendarDay | null>(null);
  const [openSession, setOpenSession] = useState<CoachCalendarSession | null>(null);
  const [openMeet, setOpenMeet] = useState<SwimCompetitionRow | null>(null);
  const [openEvent, setOpenEvent] = useState<SwimEventRow | null>(null);
  const [coaches, setCoaches] = useState<{ staff_id: string; staff_name: string }[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'kids' | 'adult' | 'lessons'>('all');

  const today = useMemo(() => toIsoDate(new Date()), []);
  const [selectedDate, setSelectedDate] = useState<string>(today);

  function filterSessions(d: CoachCalendarDay) {
    if (categoryFilter === 'lessons') return [];
    if (categoryFilter === 'all') return d.sessions ?? [];
    return (d.sessions ?? []).filter((s) => {
      const a = (s.target_audience || 'kids').toLowerCase();
      return a === categoryFilter || a === 'all';
    });
  }

  function filterLessons(d: CoachCalendarDay) {
    if (categoryFilter === 'kids' || categoryFilter === 'adult') return [];
    return d.lessons ?? [];
  }

  const { start, end } = useMemo(() => computeRange(anchor, view), [anchor, view]);

  useEffect(() => { void load(); }, [start.getTime(), end.getTime()]);

  // Swim coaches — passed to the session dialog for its coach-assignment UI.
  useEffect(() => {
    void (async () => {
      try {
        const pros = await proBookingService.getAllPros();
        setCoaches(
          pros
            .filter((p: Pro) => (p.departments ?? []).some((d) => String(d).toLowerCase() === 'swim_program'))
            .map((p: Pro) => ({ staff_id: p.id, staff_name: `${p.first_name} ${p.last_name}`.trim() }))
        );
      } catch {
        /* non-blocking */
      }
    })();
  }, []);

  // Pick a sensible selected day when the range/view changes: the viewed day (day view),
  // else today if it's in range, else the first day of the range.
  useEffect(() => {
    if (view === 'day') {
      setSelectedDate(toIsoDate(anchor));
      return;
    }
    const hasToday = days.some((d) => d.date === today);
    setSelectedDate(hasToday ? today : days[0]?.date ?? today);
  }, [days, view, anchor, today]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await swimService.coachCalendar(toIsoDate(start), toIsoDate(end));
      setDays(res.data);
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      setError(err?.response?.data?.error || err?.message || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }

  function navigate(direction: 'prev' | 'next') {
    const next = new Date(anchor);
    if (view === 'day') next.setDate(next.getDate() + (direction === 'next' ? 1 : -1));
    else if (view === 'week') next.setDate(next.getDate() + (direction === 'next' ? 7 : -7));
    else next.setMonth(next.getMonth() + (direction === 'next' ? 1 : -1));
    setAnchor(next);
  }

  // "Today" should jump to today; Week/Month keep the current anchor.
  function handleViewChange(v: ViewMode) {
    if (v === 'day') setAnchor(new Date());
    setView(v);
  }

  const currentMonth = anchor.getMonth();
  const showGrid = view === 'month' || view === 'week';
  const DOT = { practice: theme.palette.primary.main, meet: theme.palette.warning.main, social: theme.palette.info.main };

  // Agenda for the selected day (respects the category filter).
  const selDay = days.find((d) => d.date === selectedDate) || null;
  type AgendaItem = { time: string | null; title: string; sub?: string; kind: 'practice' | 'meet' | 'social'; session?: CoachCalendarSession; competition?: SwimCompetitionRow; event?: SwimEventRow };
  const agenda: AgendaItem[] = [];
  if (selDay) {
    for (const s of filterSessions(selDay)) {
      const parts: string[] = [];
      if (s.target_audience) parts.push(String(s.target_audience));
      if (s.staff?.length) parts.push(s.staff.map((x) => x.staff_name).filter(Boolean).join(', '));
      agenda.push({ time: s.start_time, title: s.program_name ?? 'Practice', sub: parts.join(' · ') || undefined, kind: 'practice', session: s });
    }
    for (const c of selDay.competitions) agenda.push({ time: c.start_time, title: c.name, sub: c.location ?? undefined, kind: 'meet', competition: c });
    for (const e of selDay.events ?? []) agenda.push({ time: e.start_time, title: e.name, sub: e.location ?? undefined, kind: 'social', event: e });
    for (const l of filterLessons(selDay)) agenda.push({ time: l.start_time, title: l.participant_label ?? 'Private lesson', sub: l.pro_name ?? undefined, kind: 'practice' });
    agenda.sort((a, b) => (a.time ?? '').localeCompare(b.time ?? ''));
  }

  const selHeader = (() => {
    if (!selDay) return view === 'day' ? 'Nothing scheduled' : 'Select a day';
    const [y, m, dd] = selDay.date.split('-').map(Number);
    const base = new Date(y, m - 1, dd).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    return selDay.competitions[0] ? `${base} · ${selDay.competitions[0].name}` : base;
  })();

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Swim Coach Schedule
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Tap any date to see the day, or to write the day's workout or schedule a competition.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }} spacing={2}>
          <QuickDateFilters currentView={view} onViewChange={handleViewChange} onNavigate={navigate} showNavigation={true} />
          <Typography variant="h6">{rangeLabel(anchor, view)}</Typography>
        </Stack>

        <ToggleButtonGroup size="small" value={categoryFilter} exclusive onChange={(_e, v) => v && setCategoryFilter(v)} sx={{ mb: 2 }}>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="kids">Kids</ToggleButton>
          <ToggleButton value="adult">Adults</ToggleButton>
          <ToggleButton value="lessons">Lessons</ToggleButton>
        </ToggleButtonGroup>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {showGrid && (
              <Card sx={{ p: { xs: 1, sm: 1.5 }, mb: 2 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
                  {DAY_LETTERS.map((h, i) => (
                    <Typography key={i} variant="caption" align="center" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {h}
                    </Typography>
                  ))}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                  {days.map((day) => {
                    const [y, m, dd] = day.date.split('-').map(Number);
                    const cellDate = new Date(y, m - 1, dd);
                    const isOtherMonth = view === 'month' && cellDate.getMonth() !== currentMonth;
                    const isToday = day.date === today;
                    const isSelected = day.date === selectedDate;
                    const hasPractice = filterSessions(day).length > 0 || filterLessons(day).length > 0;
                    const hasMeet = day.competitions.length > 0;
                    const hasSocial = (day.events?.length ?? 0) > 0;
                    return (
                      <Box
                        key={day.date}
                        onClick={() => { if (!isOtherMonth) setSelectedDate(day.date); }}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px',
                          py: 0.75,
                          cursor: isOtherMonth ? 'default' : 'pointer',
                          opacity: isOtherMonth ? 0.28 : 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: isSelected ? 'primary.main' : 'transparent',
                            color: isSelected ? 'primary.contrastText' : 'text.primary',
                            border: isToday && !isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: isToday || isSelected ? 700 : 500 }}>
                            {cellDate.getDate()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: '3px', height: 6 }}>
                          {hasPractice && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: DOT.practice }} />}
                          {hasMeet && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: DOT.meet }} />}
                          {hasSocial && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: DOT.social }} />}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                <Stack direction="row" spacing={2.5} justifyContent="center" sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                  {([['Practice', DOT.practice], ['Meet', DOT.meet], ['Social', DOT.social]] as const).map(([label, color]) => (
                    <Stack key={label} direction="row" spacing={0.75} alignItems="center">
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} />
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Card>
            )}

            {/* Selected-day agenda (the whole view in Day mode; below the grid in Week/Month) */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{selHeader}</Typography>
              {selDay && (
                <Button size="small" startIcon={<EditCalendarIcon />} onClick={() => setOpenDay(selDay)}>
                  Edit day
                </Button>
              )}
            </Box>

            {selDay?.workout ? (
              <Card sx={{ p: 2, mb: 1, borderLeft: `3px solid ${theme.palette.success.main}` }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <EventNoteIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
                  <Typography variant="subtitle2" color="success.main">Today's set</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">{selDay.workout}</Typography>
              </Card>
            ) : null}

            <Stack spacing={1}>
              {agenda.map((it, i) => {
                // Practice → session detail (workout + attendance); meet/event → day editor.
                const onClick = it.session
                  ? () => setOpenSession(it.session!)
                  : it.competition
                    ? () => setOpenMeet(it.competition!)
                    : it.event
                      ? () => setOpenEvent(it.event!)
                      : undefined;
                const clickable = !!onClick;
                return (
                  <Card
                    key={i}
                    onClick={onClick}
                    sx={{
                      p: 1.5,
                      cursor: clickable ? 'pointer' : 'default',
                      ...(it.kind === 'meet' ? { borderLeft: `3px solid ${theme.palette.warning.main}` } : {}),
                      ...(clickable ? { '&:hover': { borderColor: theme.palette.primary.main } } : {}),
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ width: 64, flexShrink: 0 }}>
                        {fmtTime(it.time) || '—'}
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{it.title}</Typography>
                        {it.sub ? <Typography variant="caption" color="text.secondary">{it.sub}</Typography> : null}
                      </Box>
                      {clickable && <ChevronRightIcon sx={{ color: 'text.disabled' }} />}
                    </Stack>
                  </Card>
                );
              })}
            </Stack>

            {selDay && agenda.length === 0 && !selDay.workout ? (
              <Card sx={{ p: 2 }}>
                <Typography color="text.secondary">Nothing scheduled. Tap “Edit day” to add a workout or a meet.</Typography>
              </Card>
            ) : null}
          </>
        )}

        {openDay && (
          <DayDetailDialog
            day={openDay}
            onClose={() => setOpenDay(null)}
            onSaved={() => {
              setOpenDay(null);
              void load();
            }}
          />
        )}

        {openSession && (
          <SessionDetailDialog
            date={selectedDate}
            session={openSession}
            coaches={coaches}
            dailyNote={selDay?.workout ?? null}
            onClose={() => setOpenSession(null)}
            onSaved={() => {
              setOpenSession(null);
              void load();
            }}
          />
        )}

        {openMeet && <MeetDetailDialog competition={openMeet} onClose={() => setOpenMeet(null)} />}
        {openEvent && <EventDetailDialog event={openEvent} onClose={() => setOpenEvent(null)} />}
      </Box>
    </Container>
  );
};

export default CoachSchedulePage;
